import { z } from "zod";
import {
  ActionEdit, editAction, reviewFigures, groundedNumbers, inventedIn, Metric, ReviewEvidence, REVIEW_LIMITS,
  type ReviewAction, type ReviewBody,
} from "./review.js";

/**
 * The conversation (35 §conversation, ADR 0063): one service for every place the owner talks to Joogoos about their
 * marketing, starting with the review — and written so the weekly voice call is the same turns arriving another way.
 *
 * What is shared is small on purpose: a turn is a role, a channel, some words and — on Joogoos's side — at most one
 * proposed change to the thing being talked about. The subject says what that thing is. Each subject brings its own
 * context and its own idea of a change; the review is the first and so far the only one.
 *
 * **A turn never decides.** It can explain the review, answer a question about it from the evidence, and propose a
 * change to the recommendation; the change is shown with a button and only the owner's press uses it. Approving,
 * changing and saying no stay free buttons beside the conversation, so the product works the same with no model at
 * all (ADR 0031 point 4, and the lesson of the Business chat the owner removed as slow, paid and redundant: ADR 0049).
 *
 * **Voice is a channel, not a second conversation.** A spoken turn is transcribed into the same `said`, answered by
 * the same operation, and the reply is spoken from the same text (35 §voice) — so the text box is the fallback that
 * always works, and the limits below hold for both.
 */

export const ConversationSubject = z.enum(["review"]);
export type ConversationSubject = z.infer<typeof ConversationSubject>;
export const TurnRole = z.enum(["owner", "joogoos"]);
export type TurnRole = z.infer<typeof TurnRole>;
export const TurnChannel = z.enum(["text", "voice"]);
export type TurnChannel = z.infer<typeof TurnChannel>;

/** What the owner sends: words, typed or (later) transcribed, never more than a short message's worth. */
export const TurnAsk = z.object({ text: z.string().trim().min(1, "say something first").max(REVIEW_LIMITS.said) });
export type TurnAsk = z.infer<typeof TurnAsk>;

/** The recommendation as the turn sees it: what it can talk about, and what a proposed change would replace. */
const Recommendation = z.object({
  kind: z.string(),
  title: z.string(),
  detail: z.string(),
  why: z.string(),
  metric: Metric,
  reviewAfterDays: z.number().int(),
  cost: z.string(),
  experiment: z.boolean(),
});

/** What `review_turn` is given: the business, the evidence, the review as the owner reads it, and the conversation. */
export const ReviewTurnInput = z.object({
  business: z.string().max(20_000),
  evidence: ReviewEvidence,
  review: z.object({
    happened: z.array(z.string()),
    uncertain: z.array(z.string()),
    diagnosis: z.string(),
    recommendation: Recommendation,
    alternatives: z.array(z.string()).max(2),
    /** Whether the owner has decided yet: a change can only be proposed to a recommendation still waiting for one. */
    decision: z.enum(["proposed", "approved", "rejected"]),
  }),
  history: z.array(z.object({ role: TurnRole, text: z.string() })).max(REVIEW_LIMITS.turnsPerReview * 2),
  said: z.string().max(REVIEW_LIMITS.said),
});
export type ReviewTurnInput = z.infer<typeof ReviewTurnInput>;

/** What the model answers: a reply, and at most one change to propose. Loose on lengths; `applyReviewTurn` clips. */
export const ReviewTurnOutput = z.object({
  reply: z.string(),
  change: z.object({ title: z.string(), detail: z.string(), reviewAfterDays: z.number(), metric: Metric }).nullable(),
});
export type ReviewTurnOutput = z.infer<typeof ReviewTurnOutput>;

/** Said instead of a reply that quoted a figure nobody counted. Better a dull sentence than a made-up number. */
export const NOT_GROUNDED = "I would have had to make a number up to answer that, so I have not. Ask it another way, or look at what the review counted above.";

/** The input a turn is given, built from what is stored. One function, so the screen's turn and a test's agree. */
export function reviewTurnInput(args: {
  business: string;
  evidence: ReviewEvidence;
  body: ReviewBody;
  action: ReviewAction;
  decision: "proposed" | "approved" | "rejected";
  history: Array<{ role: TurnRole; text: string }>;
  said: string;
}): ReviewTurnInput {
  const a = args.action;
  return {
    business: args.business,
    evidence: args.evidence,
    review: {
      happened: args.body.happened, uncertain: args.body.uncertain, diagnosis: args.body.diagnosis,
      recommendation: {
        kind: a.kind, title: a.title, detail: a.detail, why: a.why, metric: a.metric, reviewAfterDays: a.reviewAfterDays,
        cost: a.cost.usd === null ? a.cost.note : `$${a.cost.usd}. ${a.cost.note}`, experiment: a.experiment,
      },
      // Every option but the one standing, the first recommendation included once another option was taken.
      alternatives: args.body.actions.slice(0, 3).filter((x) => x.kind !== a.kind || x.title !== a.title).slice(0, 2).map((x) => x.title),
      decision: args.decision,
    },
    history: args.history.slice(-REVIEW_LIMITS.turnsPerReview * 2),
    said: args.said,
  };
}

/**
 * What survives a turn. The reply is held to the evidence as strictly as the review's own sentences — a figure in it
 * must be one the evidence or the recommendation holds, or one in the change it offers — because an answer to "did I
 * get forty enquiries?" must not be allowed to say yes by repeating the question. The owner's own figures may appear
 * only in a proposed change ("post three times next week"), which passes the same check an owner's own edit does and
 * is only offered while the recommendation is still waiting for a decision.
 */
export function applyReviewTurn(
  raw: unknown, e: ReviewEvidence, action: ReviewAction, decision: "proposed" | "approved" | "rejected",
  /** What the owner has said in this conversation: a figure they wrote may go into a change they asked for. */
  ownerSaid = "",
): { reply: string; change: ActionEdit | null; refused: string[] } {
  const refused: string[] = [];
  const parsed = ReviewTurnOutput.safeParse(raw);
  if (!parsed.success) return { reply: NOT_GROUNDED, change: null, refused: ["the answer was not a turn"] };
  const known = new Set([...groundedNumbers(e), ...reviewFigures(JSON.stringify(action))]);

  let change: ActionEdit | null = null;
  const c = parsed.data.change;
  if (c && decision !== "proposed") refused.push("change: the owner has already decided");
  else if (c) {
    const edit = ActionEdit.safeParse({ title: c.title.slice(0, 120), detail: c.detail.slice(0, 500), reviewAfterDays: Math.round(c.reviewAfterDays), metric: c.metric });
    const made = edit.success ? inventedIn(`${edit.data.title} ${edit.data.detail}`, new Set([...known, ...reviewFigures(ownerSaid)]), true) : [];
    if (!edit.success) refused.push("change: not a change that can be made");
    else if (made.length) refused.push(`change: ${made.join(", ")} is not in the evidence`);
    else {
      const tried = editAction(action, edit.data, e);
      if ("error" in tried) refused.push(`change: ${tried.error}`);
      else change = { title: tried.action.title, detail: tried.action.detail, reviewAfterDays: tried.action.reviewAfterDays, metric: tried.action.metric };
    }
  }

  // The reply may talk about the change it offers, so the change's own figures are the reply's too — once it stood.
  const allowed = change ? new Set([...known, ...reviewFigures(JSON.stringify(change))]) : known;
  let reply = parsed.data.reply.replace(/\s+/g, " ").trim().slice(0, 1_200);
  const madeUp = inventedIn(reply, allowed);
  if (madeUp.length) { refused.push(`reply: ${madeUp.join(", ")} is not in the evidence`); reply = NOT_GROUNDED; }
  if (!reply) reply = NOT_GROUNDED;
  return { reply, change, refused };
}
